import { API_BASE_URL } from "@/lib/env";

export interface APIPodcast {
  podcast_slug: string;
  display_name: string;
  emoji: string;
  description: string | null;
  episode_count: number;
  latest_episode_date: string | null;
  taxonomy: {
    Format?: string[];
    Theme?: string[];
    Topic?: string[];
  } | null;
}

export interface APIEpisode {
  id: string;
  title: string;
  description: string | null;
  episode_number: number;
  audio_url: string;
  duration: number | null;
  published_date: string | null;
  podcast_slug: string;
  podcast_title: string | null;
  podcast_display_name: string | null;
  is_indexed: boolean;
  tags: string | { Theme?: string[]; Topic?: string[]; Format?: string[] } | null;
}

export interface AskRequest {
  question: string;
  previous_response_id?: string;
  k?: number;
  max_context_tokens?: number;
  mmr_diversity?: number;
  score_threshold?: number;
}

export interface AskResponse {
  answer: string;
  verdict: "answer" | "insufficient";
  used_context: "current" | "previous" | "both";
  missing: string[];
  response_id: string;
  model: string;
  chunks_used: number;
  referenced_episode_numbers?: number[];
}

export interface APISubscription {
  podcast_slug: string;
  notify_enabled: boolean;
  feed_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  display_name?: string | null;
  emoji?: string | null;
  podcast?: {
    display_name?: string | null;
    emoji?: string | null;
    description?: string | null;
  } | null;
}

export interface EpisodeCountResponse {
  podcast_slug: string;
  display_name: string;
  episode_count: number;
  cached_at?: string | null;
  refreshed?: boolean;
}

export interface SubscribeToFeedResponse {
  ok: boolean;
  subscribed?: {
    slug: string;
    accepted_url: string;
    primary_key: string | null;
    matched_by: string | null;
  };
  status?: string;
  error?: string | null;
}

export interface UpdateSubscriptionNotificationResponse {
  ok: boolean;
  subscription?: APISubscription | null;
  error?: string | null;
}

type UnauthorizedHandler = () => void;
type TokenProvider = () => string | null;

class APIClient {
  private readonly baseURL: string;
  private tokenProvider: TokenProvider = () => null;
  private unauthorizedHandler: UnauthorizedHandler | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  setTokenProvider(provider: TokenProvider) {
    this.tokenProvider = provider;
  }

  setUnauthorizedHandler(handler: UnauthorizedHandler) {
    this.unauthorizedHandler = handler;
  }

  async authorizedFetch(path: string, init: RequestInit = {}) {
    const headers = new Headers(init.headers ?? {});
    const token = this.tokenProvider();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(path, {
      ...init,
      headers,
      cache: "no-store",
    });

    if (response.status === 401 && this.unauthorizedHandler) {
      this.unauthorizedHandler();
    }

    return response;
  }

  private async parseJSON<T>(response: Response, errorMessage: string): Promise<T> {
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`${errorMessage}: ${response.status} ${response.statusText}${body ? ` - ${body}` : ""}`);
    }
    return response.json() as Promise<T>;
  }

  async fetchPodcasts(): Promise<APIPodcast[]> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/podcasts`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<APIPodcast[]>(response, "Failed to fetch podcasts");
  }

  async fetchEpisodes(podcastSlug?: string): Promise<APIEpisode[]> {
    const url = podcastSlug
      ? `${this.baseURL}/me/episodes?podcast_slug=${encodeURIComponent(podcastSlug)}`
      : `${this.baseURL}/me/episodes`;

    const response = await this.authorizedFetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<APIEpisode[]>(response, "Failed to fetch episodes");
  }

  async askPodcast(podcastSlug: string, payload: AskRequest): Promise<AskResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/podcasts/${encodeURIComponent(podcastSlug)}/ask`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    return this.parseJSON<AskResponse>(response, "Failed to ask question");
  }

  async fetchSubscriptions(): Promise<APISubscription[]> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/subscriptions`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<APISubscription[]>(response, "Failed to fetch subscriptions");
  }

  async updateSubscriptionNotification(
    podcastSlug: string,
    enabled: boolean,
  ): Promise<UpdateSubscriptionNotificationResponse> {
    const response = await this.authorizedFetch(
      `${this.baseURL}/me/subscriptions/${encodeURIComponent(podcastSlug)}?enabled=${enabled}`,
      {
        method: "PATCH",
        headers: { Accept: "application/json" },
      },
    );

    if (response.status === 204) {
      return { ok: true, subscription: null };
    }

    return this.parseJSON<UpdateSubscriptionNotificationResponse>(response, "Failed to update subscription");
  }

  async resolveEpisodeCount(feedUrl: string) {
    const response = await this.authorizedFetch(`${this.baseURL}/me/podcasts/episode-count`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        feed_url: feedUrl.trim(),
      }),
    });

    return this.parseJSON<EpisodeCountResponse>(response, "Failed to resolve podcast feed");
  }

  async subscribeToFeed(
    feedUrl: string,
    options?: { notifyEnabled?: boolean; idempotencyKey?: string },
  ) {
    const payload: Record<string, unknown> = {
      feed_url: feedUrl.trim(),
      notify_enabled: options?.notifyEnabled ?? true,
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (options?.idempotencyKey) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }

    const response = await this.authorizedFetch(`${this.baseURL}/me/subscriptions/rss`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    return this.parseJSON<SubscribeToFeedResponse>(response, "Failed to subscribe to podcast");
  }

  async createCheckoutSession(planKey: string) {
    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        plan_key: planKey,
        success_url: `${window.location.origin}/billing/success`,
        cancel_url: `${window.location.origin}/paywall`,
      }),
    });

    return this.parseJSON<{ session_id: string; checkout_url: string }>(
      response,
      "Failed to create checkout session",
    );
  }

  async getCreditGrantSummary(): Promise<CreditGrantSummary> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/credit-grant-summary`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<CreditGrantSummary>(response, "Failed to fetch credit summary");
  }

  async topupCredits(amountCents: number): Promise<TopupResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/billing/topup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        credit_type: "chat",
        amount_cents: amountCents,
      }),
    });

    return this.parseJSON<TopupResponse>(response, "Failed to top up credits");
  }

  async updatePaymentMethod(successUrl: string, cancelUrl: string): Promise<PaymentMethodSessionResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/payment-method`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    });

    return this.parseJSON<PaymentMethodSessionResponse>(response, "Failed to create payment method session");
  }

  async getBillingPlan(): Promise<BillingPlanResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/plan`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<BillingPlanResponse>(response, "Failed to fetch billing plan");
  }

  async changePlan(
    newPlanKey: string,
    subscriptionId?: string,
    componentConfigurations?: ComponentConfiguration[],
  ): Promise<ChangePlanResponse> {
    const body: Record<string, unknown> = { new_plan_key: newPlanKey };
    if (subscriptionId) body.subscription_id = subscriptionId;
    if (componentConfigurations) body.component_configurations = componentConfigurations;

    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/change-plan`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    return this.parseJSON<ChangePlanResponse>(response, "Failed to change plan");
  }

  async cancelPlan(subscriptionId?: string): Promise<CancelPlanResponse> {
    const body: Record<string, unknown> = {};
    if (subscriptionId) body.subscription_id = subscriptionId;

    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/cancel-plan`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    return this.parseJSON<CancelPlanResponse>(response, "Failed to cancel plan");
  }

  // One-Off Session Methods

  async previewFeed(
    params: { feed_url: string } | { podcast_slug: string }
  ): Promise<FeedPreview> {
    const body = "feed_url" in params
      ? { feed_url: params.feed_url.trim() }
      : { podcast_slug: params.podcast_slug };

    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/preview`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    return this.parseJSON<FeedPreview>(response, "Failed to preview feed");
  }

  async createOneOffSession(
    params:
      | { feed_url: string; episode_indices: number[] }
      | { podcast_slug: string; episode_numbers: number[] }
  ): Promise<OneOffSession> {
    const body = "feed_url" in params
      ? { feed_url: params.feed_url.trim(), episode_indices: params.episode_indices }
      : { podcast_slug: params.podcast_slug, episode_numbers: params.episode_numbers };

    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    return this.parseJSON<OneOffSession>(response, "Failed to create session");
  }

  async getOneOffSession(): Promise<OneOffSession> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions/current`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<OneOffSession>(response, "Failed to fetch session");
  }

  async askOneOffSession(payload: OneOffAskRequest): Promise<OneOffAskResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions/current/ask`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    return this.parseJSON<OneOffAskResponse>(response, "Failed to ask question");
  }

  async deleteOneOffSession(): Promise<void> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions/current`, {
      method: "DELETE",
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete session: ${response.status}`);
    }
  }
}

export const apiClient = new APIClient();

export default APIClient;

export interface CreditGrantSummary {
  balance_cents: number;
  balance_formatted?: string;
  currency?: string;
}

export interface TopupResponse {
  invoice_id: string;
  payment_status: string;
  amount_cents: number;
  credit_type: string;
}

export interface PaymentMethodSessionResponse {
  session_id: string;
  checkout_url: string;
}

export interface BillingPlanResponse {
  subscription_status: "active" | "pending" | "canceled" | "past_due";
  stripe_plan_lookup_key: string | null;
  pricing_plan_subscription_id: string | null;
  stripe_customer_id: string | null;
}

export interface ComponentConfiguration {
  lookup_key: string;
  quantity: number;
}

export interface ChangePlanResponse {
  ok: boolean;
}

export interface CancelPlanResponse {
  ok: boolean;
}

// One-Off Session Types
export interface FeedPreviewEpisode {
  index: number;
  title: string;
  description: string;
  duration_seconds: number | null;
  published_date: string | null;
  audio_url?: string;
  episode_number?: number;      // present for cached/subscription sources
  has_transcript?: boolean;     // true if transcript exists in system
}

export interface FeedPreview {
  feed_title: string;
  feed_description: string;
  episodes: FeedPreviewEpisode[];
  podcast_slug?: string;        // present if recognized/cached
  source: "feed" | "cached" | "subscription";
}

export interface OneOffSessionEpisode {
  index: number;
  title: string;
  status: "queued" | "in_progress" | "ready" | "failed";
  token_count: number | null;
  error?: string;
}

export interface OneOffSession {
  session_id: string;
  status: "transcribing" | "ready" | "partial" | "failed" | "expired";
  feed_title: string;
  episodes: OneOffSessionEpisode[];
  total_tokens?: number;
  model_context_limit?: number;
  expires_at: string;
}

export interface OneOffAskRequest {
  question: string;
  include_episodes?: number[];
  previous_response_id?: string;
}

export interface OneOffAskResponse {
  verdict: "answer" | "insufficient";
  answer: string;
  response_id: string;
  model: string;
  chunks_used: number;
  usage: {
    input_tokens: number;
    output_tokens: number;
    context_reset: boolean;
    cost_usd?: number;
  };
}
